/**
 * Shared "Last updated" helpers used across Monthly reviews, Fiscal end
 * review, and Program Changes. Centralising these guarantees the same format
 * and fallback wording everywhere a "Last updated" pill is rendered.
 */

export interface LastUpdatedDisplayInfo {
  at: string;
  by: string;
}

/**
 * Reduces a collection of per-row last-updated infos to the single most-recent
 * one. Skips entries with malformed timestamps so one bad record can't poison
 * the header for the whole page.
 */
export const pickMostRecent = (
  infos: ReadonlyArray<LastUpdatedDisplayInfo | undefined>,
): LastUpdatedDisplayInfo | undefined => {
  let best: LastUpdatedDisplayInfo | undefined;
  let bestMs = -Infinity;
  for (const info of infos) {
    if (!info) continue;
    const ms = new Date(info.at).getTime();
    if (Number.isNaN(ms)) continue;
    if (ms > bestMs) {
      bestMs = ms;
      best = info;
    }
  }
  return best;
};

export const LAST_UPDATED_EMPTY_LABEL = 'No updates made yet';
export const LAST_UPDATED_INVALID_LABEL = 'Invalid last-updated timestamp';

const LAST_UPDATED_TIME_ZONE = 'America/Los_Angeles';

/**
 * Formats a timestamp using the Monthly reviews style:
 *   "May 28, 2026 12:10 AM (PDT)"
 *
 * The timestamp is rendered in Pacific time and the zone abbreviation
 * (PDT in summer, PST in winter) is derived dynamically from
 * `Intl.DateTimeFormat` so it always matches the actual offset for the given date.
 * The zone is rendered explicitly so reviewers in different timezones interpret the
 * value the same way (audit-friendly). Returns the canonical empty label for
 * null/undefined and the invalid label for unparseable inputs.
 */
export const formatLastUpdatedLabel = (
  at: string | Date | null | undefined,
): string => {
  if (at === null || at === undefined || at === '') {
    return LAST_UPDATED_EMPTY_LABEL;
  }
  const date = at instanceof Date ? at : new Date(at);
  if (Number.isNaN(date.getTime())) {
    return LAST_UPDATED_INVALID_LABEL;
  }
  const datePart = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: LAST_UPDATED_TIME_ZONE,
  });
  const timePart = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: LAST_UPDATED_TIME_ZONE,
  });
  const tzAbbr =
    new Intl.DateTimeFormat('en-US', {
      timeZone: LAST_UPDATED_TIME_ZONE,
      timeZoneName: 'short',
    })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value ?? 'PT';
  return `${datePart} ${timePart} (${tzAbbr})`;
};
